/**
 * Generated by orval 🍺
 * Do not edit manually.
 * DevSMS API v2
 * The dashboard's API (LLD §26.2), and the source Orval generates the
 * dashboard's client from (`dashboard/orval.config.ts`).
 *
 * This document describes the Go server as built. It is held to the code
 * by `test/contract`: the routes it lists are exactly the routes served,
 * in normal and in standalone mode (`TestV2Routes_MatchTheSpecInBothModes`),
 * and every answer the integration, contract and end-to-end tests provoke
 * is validated against the operation it documents
 * (`test/harness/apispec.Validating`). Spectral lints it with
 * `api/.spectral.yaml` (`make spec`).
 *
 * Conventions:
 *   - JSON is camelCase; enums are snake_case; times are RFC 3339 UTC.
 *   - Money is a whole number of XAF (ISO 4217 exponent 0) in a plain
 *     integer field, with a sibling `currency` field (always `XAF`) on
 *     the objects that carry one. There is no `{amount, currency}`
 *     wrapper object (DV-145).
 *   - Lists that can grow are keyset-paged: `?cursor=&limit=` in,
 *     `{items, nextCursor, hasMore}` out. A cursor is opaque and signed;
 *     a tampered one answers 422 `invalid_cursor`. Short, bounded lists
 *     answer `{items}`.
 *   - Errors are RFC 9457 `application/problem+json`. The machine code is
 *     the last segment of `type`, in kebab case
 *     (`…/problems/wallet-insufficient` is the code `wallet_insufficient`);
 *     `x-problem-codes` lists every code with its status. There is no
 *     separate `code` member (DV-145).
 *   - Authentication is a bearer access token from `POST /auth/login`
 *     (or Google, TOTP, refresh, invite). API keys are not accepted on
 *     v2; they are the v1 credential.
 *   - `x-permission` names the permission an operation requires (every
 *     one listed, when it is a list). Account routes (`/account/*`) also
 *     admit any staff user without it (DV-55). Every `/admin/*` route
 *     also refuses a staff user who has not enrolled TOTP (403
 *     `totp_enrolment_required`).
 *   - `x-step-up: true` marks an operation that needs an `X-Step-Up`
 *     token from `POST /auth/step-up`, under 5 minutes old (403
 *     `step_up_required` otherwise).
 *   - `x-idempotency: required` marks an operation that needs an
 *     `Idempotency-Key` header (422 `idempotency_key_required`); a
 *     repeat replays the first answer byte for byte with
 *     `Idempotent-Replay: true`. `x-idempotency: exempt` marks a write
 *     in a money area that deliberately takes none, and
 *     `x-idempotency-reason` says why.
 *   - `x-standalone: absent` marks an operation that is not mounted when
 *     `STANDALONE_MODE=true` (it answers 404 there).
 *   - `?lang=fr|en` (or `Accept-Language`) chooses the language of every
 *     problem's `detail` and of the PDFs; French is the default.
 *   - CORS admits one origin, the deployment's `DASHBOARD_URL`, with
 *     credentials (none when it is unset; never `*`). Preflights are
 *     answered `204` before authentication and rate limiting. Allowed
 *     request headers: `Authorization`, `Content-Type`,
 *     `Accept-Language`, `Idempotency-Key`, `X-Step-Up`, `X-Sdk-Client`;
 *     exposed: `Idempotent-Replay`, `RateLimit-Limit`,
 *     `RateLimit-Remaining`, `RateLimit-Reset`, `Retry-After`,
 *     `X-Request-Id`, `Content-Disposition`. (`/api/v1` keeps Nest's
 *     `enableCors()`: `Access-Control-Allow-Origin: *`, no credentials.)
 *
 * OpenAPI spec version: 2.0.0
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseInfiniteQueryResult,
  DefinedUseQueryResult,
  InfiniteData,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import type {
  AdminDevicePage,
  AdminListDevicesParams,
  AdminUpdateDeviceBody,
  Device,
  DeviceHealth,
  DevicePage,
  ForbiddenResponse,
  InternalErrorResponse,
  ListDevicesParams,
  NotFoundResponse,
  PayloadTooLargeResponse,
  TooManyRequestsResponse,
  UnauthorizedResponse,
  UnavailableResponse,
  UnprocessableResponse,
  UpdateDeviceBody
} from '../model';

import { apiFetch } from '../../mutator.ts';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getListDevicesUrl = (params?: ListDevicesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/devices?${stringifiedParams}` : `/devices`
}

/**
 * @summary The caller's devices, newest first
 */
export const listDevices = async (params?: ListDevicesParams, options?: Parameters<typeof apiFetch>[1]): Promise<DevicePage> => {

  return apiFetch<DevicePage>(getListDevicesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDevicesInfiniteQueryKey = (params?: ListDevicesParams,) => {
    return [
    'infinite', `/devices`, ...(params ? [params] : [])
    ] as const;
    }

export const getListDevicesQueryKey = (params?: ListDevicesParams,) => {
    return [
    `/devices`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDevicesInfiniteQueryOptions = <TData = InfiniteData<Awaited<ReturnType<typeof listDevices>>, ListDevicesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: ListDevicesParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listDevices>>, TError, TData, QueryKey, ListDevicesParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDevicesInfiniteQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDevices>>, QueryKey, ListDevicesParams['cursor']> = ({ signal, pageParam }) => listDevices({...params, ...((c) => c === undefined ? {} : { 'cursor': c })(pageParam ?? params?.['cursor'])}, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof listDevices>>, TError, TData, QueryKey, ListDevicesParams['cursor']> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListDevicesInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof listDevices>>>
export type ListDevicesInfiniteQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useListDevicesInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listDevices>>, ListDevicesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  ListDevicesParams, options: { query:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listDevices>>, TError, TData, QueryKey, ListDevicesParams['cursor']>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listDevices>>,
          TError,
          Awaited<ReturnType<typeof listDevices>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListDevicesInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listDevices>>, ListDevicesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListDevicesParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listDevices>>, TError, TData, QueryKey, ListDevicesParams['cursor']>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listDevices>>,
          TError,
          Awaited<ReturnType<typeof listDevices>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListDevicesInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listDevices>>, ListDevicesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListDevicesParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listDevices>>, TError, TData, QueryKey, ListDevicesParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The caller's devices, newest first
 */

export function useListDevicesInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listDevices>>, ListDevicesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListDevicesParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listDevices>>, TError, TData, QueryKey, ListDevicesParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListDevicesInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions, queryClient) as  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getListDevicesQueryOptions = <TData = Awaited<ReturnType<typeof listDevices>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: ListDevicesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listDevices>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDevicesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDevices>>> = ({ signal }) => listDevices(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDevices>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListDevicesQueryResult = NonNullable<Awaited<ReturnType<typeof listDevices>>>
export type ListDevicesQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useListDevices<TData = Awaited<ReturnType<typeof listDevices>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  ListDevicesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listDevices>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listDevices>>,
          TError,
          Awaited<ReturnType<typeof listDevices>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListDevices<TData = Awaited<ReturnType<typeof listDevices>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListDevicesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listDevices>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listDevices>>,
          TError,
          Awaited<ReturnType<typeof listDevices>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListDevices<TData = Awaited<ReturnType<typeof listDevices>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListDevicesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listDevices>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The caller's devices, newest first
 */

export function useListDevices<TData = Awaited<ReturnType<typeof listDevices>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListDevicesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listDevices>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListDevicesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getGetDeviceUrl = (id: string,) => {




  return `/devices/${id}`
}

/**
 * @summary One device with every status a heartbeat reported (the health panel)
 */
export const getDevice = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<DeviceHealth> => {

  return apiFetch<DeviceHealth>(getGetDeviceUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDeviceQueryKey = (id: string,) => {
    return [
    `/devices/${id}`
    ] as const;
    }


export const getGetDeviceQueryOptions = <TData = Awaited<ReturnType<typeof getDevice>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDevice>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDeviceQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDevice>>> = ({ signal }) => getDevice(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDevice>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetDeviceQueryResult = NonNullable<Awaited<ReturnType<typeof getDevice>>>
export type GetDeviceQueryError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useGetDevice<TData = Awaited<ReturnType<typeof getDevice>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDevice>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDevice>>,
          TError,
          Awaited<ReturnType<typeof getDevice>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetDevice<TData = Awaited<ReturnType<typeof getDevice>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDevice>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDevice>>,
          TError,
          Awaited<ReturnType<typeof getDevice>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetDevice<TData = Awaited<ReturnType<typeof getDevice>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDevice>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary One device with every status a heartbeat reported (the health panel)
 */

export function useGetDevice<TData = Awaited<ReturnType<typeof getDevice>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDevice>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetDeviceQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getUpdateDeviceUrl = (id: string,) => {




  return `/devices/${id}`
}

/**
 * Enabling one past the plan's device limit is refused with 429 `device_limit_reached`.
 * @summary Rename, enable or disable a device
 */
export const updateDevice = async (id: string,
    updateDeviceBody: UpdateDeviceBody, options?: Parameters<typeof apiFetch>[1]): Promise<Device> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Device>(getUpdateDeviceUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateDeviceBody)
  }
);}





export const getUpdateDeviceMutationKey = () => ['updateDevice'] as const;

export const getUpdateDeviceMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDevice>>, TError,UpdateDeviceMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateDevice>>, TError,UpdateDeviceMutationVariables, TContext> => {

const mutationKey = getUpdateDeviceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateDevice>>, UpdateDeviceMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateDevice(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateDeviceMutationResult = NonNullable<Awaited<ReturnType<typeof updateDevice>>>
    export type UpdateDeviceMutationBody = UpdateDeviceBody
    export type UpdateDeviceMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse
    export type UpdateDeviceMutationVariables = {id: string;data: UpdateDeviceBody}

    /**
 * @summary Rename, enable or disable a device
 */
export const useUpdateDevice = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateDevice>>, TError,UpdateDeviceMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateDevice>>,
        TError,
        UpdateDeviceMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateDeviceMutationOptions(options), queryClient);
    }
    export const getDeleteDeviceUrl = (id: string,) => {




  return `/devices/${id}`
}

/**
 * @summary Delete a device
 */
export const deleteDevice = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<void> => {

  return apiFetch<void>(getDeleteDeviceUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteDeviceMutationKey = () => ['deleteDevice'] as const;

export const getDeleteDeviceMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteDevice>>, TError,DeleteDeviceMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteDevice>>, TError,DeleteDeviceMutationVariables, TContext> => {

const mutationKey = getDeleteDeviceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteDevice>>, DeleteDeviceMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteDevice(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteDeviceMutationResult = NonNullable<Awaited<ReturnType<typeof deleteDevice>>>

    export type DeleteDeviceMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type DeleteDeviceMutationVariables = {id: string}

    /**
 * @summary Delete a device
 */
export const useDeleteDevice = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteDevice>>, TError,DeleteDeviceMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteDevice>>,
        TError,
        DeleteDeviceMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteDeviceMutationOptions(options), queryClient);
    }
    export const getSetDefaultDeviceUrl = (id: string,) => {




  return `/devices/${id}/set-default`
}

/**
 * @summary Make a device the one sends use by default
 */
export const setDefaultDevice = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<Device> => {

  return apiFetch<Device>(getSetDefaultDeviceUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getSetDefaultDeviceMutationKey = () => ['setDefaultDevice'] as const;

export const getSetDefaultDeviceMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setDefaultDevice>>, TError,SetDefaultDeviceMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setDefaultDevice>>, TError,SetDefaultDeviceMutationVariables, TContext> => {

const mutationKey = getSetDefaultDeviceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setDefaultDevice>>, SetDefaultDeviceMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  setDefaultDevice(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetDefaultDeviceMutationResult = NonNullable<Awaited<ReturnType<typeof setDefaultDevice>>>

    export type SetDefaultDeviceMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type SetDefaultDeviceMutationVariables = {id: string}

    /**
 * @summary Make a device the one sends use by default
 */
export const useSetDefaultDevice = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setDefaultDevice>>, TError,SetDefaultDeviceMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setDefaultDevice>>,
        TError,
        SetDefaultDeviceMutationVariables,
        TContext
      > => {
      return useMutation(getSetDefaultDeviceMutationOptions(options), queryClient);
    }
    export const getAdminListDevicesUrl = (params?: AdminListDevicesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/admin/devices?${stringifiedParams}` : `/admin/devices`
}

/**
 * @summary Every device, newest first
 */
export const adminListDevices = async (params?: AdminListDevicesParams, options?: Parameters<typeof apiFetch>[1]): Promise<AdminDevicePage> => {

  return apiFetch<AdminDevicePage>(getAdminListDevicesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminListDevicesInfiniteQueryKey = (params?: AdminListDevicesParams,) => {
    return [
    'infinite', `/admin/devices`, ...(params ? [params] : [])
    ] as const;
    }

export const getAdminListDevicesQueryKey = (params?: AdminListDevicesParams,) => {
    return [
    `/admin/devices`, ...(params ? [params] : [])
    ] as const;
    }


export const getAdminListDevicesInfiniteQueryOptions = <TData = InfiniteData<Awaited<ReturnType<typeof adminListDevices>>, AdminListDevicesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: AdminListDevicesParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListDevices>>, TError, TData, QueryKey, AdminListDevicesParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListDevicesInfiniteQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListDevices>>, QueryKey, AdminListDevicesParams['cursor']> = ({ signal, pageParam }) => adminListDevices({...params, ...((c) => c === undefined ? {} : { 'cursor': c })(pageParam ?? params?.['cursor'])}, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListDevices>>, TError, TData, QueryKey, AdminListDevicesParams['cursor']> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListDevicesInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof adminListDevices>>>
export type AdminListDevicesInfiniteQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListDevicesInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListDevices>>, AdminListDevicesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  AdminListDevicesParams, options: { query:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListDevices>>, TError, TData, QueryKey, AdminListDevicesParams['cursor']>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListDevices>>,
          TError,
          Awaited<ReturnType<typeof adminListDevices>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListDevicesInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListDevices>>, AdminListDevicesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListDevicesParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListDevices>>, TError, TData, QueryKey, AdminListDevicesParams['cursor']>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListDevices>>,
          TError,
          Awaited<ReturnType<typeof adminListDevices>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListDevicesInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListDevices>>, AdminListDevicesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListDevicesParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListDevices>>, TError, TData, QueryKey, AdminListDevicesParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Every device, newest first
 */

export function useAdminListDevicesInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListDevices>>, AdminListDevicesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListDevicesParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListDevices>>, TError, TData, QueryKey, AdminListDevicesParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListDevicesInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions, queryClient) as  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminListDevicesQueryOptions = <TData = Awaited<ReturnType<typeof adminListDevices>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: AdminListDevicesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListDevices>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListDevicesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListDevices>>> = ({ signal }) => adminListDevices(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminListDevices>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListDevicesQueryResult = NonNullable<Awaited<ReturnType<typeof adminListDevices>>>
export type AdminListDevicesQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListDevices<TData = Awaited<ReturnType<typeof adminListDevices>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  AdminListDevicesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListDevices>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListDevices>>,
          TError,
          Awaited<ReturnType<typeof adminListDevices>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListDevices<TData = Awaited<ReturnType<typeof adminListDevices>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListDevicesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListDevices>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListDevices>>,
          TError,
          Awaited<ReturnType<typeof adminListDevices>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListDevices<TData = Awaited<ReturnType<typeof adminListDevices>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListDevicesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListDevices>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Every device, newest first
 */

export function useAdminListDevices<TData = Awaited<ReturnType<typeof adminListDevices>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListDevicesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListDevices>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListDevicesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminUpdateDeviceUrl = (id: string,) => {




  return `/admin/devices/${id}`
}

/**
 * @summary Enable or disable any device
 */
export const adminUpdateDevice = async (id: string,
    adminUpdateDeviceBody: AdminUpdateDeviceBody, options?: Parameters<typeof apiFetch>[1]): Promise<Device> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<Device>(getAdminUpdateDeviceUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(adminUpdateDeviceBody)
  }
);}





export const getAdminUpdateDeviceMutationKey = () => ['adminUpdateDevice'] as const;

export const getAdminUpdateDeviceMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminUpdateDevice>>, TError,AdminUpdateDeviceMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adminUpdateDevice>>, TError,AdminUpdateDeviceMutationVariables, TContext> => {

const mutationKey = getAdminUpdateDeviceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adminUpdateDevice>>, AdminUpdateDeviceMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  adminUpdateDevice(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdminUpdateDeviceMutationResult = NonNullable<Awaited<ReturnType<typeof adminUpdateDevice>>>
    export type AdminUpdateDeviceMutationBody = AdminUpdateDeviceBody
    export type AdminUpdateDeviceMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse
    export type AdminUpdateDeviceMutationVariables = {id: string;data: AdminUpdateDeviceBody}

    /**
 * @summary Enable or disable any device
 */
export const useAdminUpdateDevice = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminUpdateDevice>>, TError,AdminUpdateDeviceMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof adminUpdateDevice>>,
        TError,
        AdminUpdateDeviceMutationVariables,
        TContext
      > => {
      return useMutation(getAdminUpdateDeviceMutationOptions(options), queryClient);
    }
